import { useEffect, useRef, useState } from "react";

import { fetchAISuggestions, type AISuggestions, type MedicationDraft } from "@/lib/api";
import { findMedByName, type MedLookupEntry } from "@/lib/med-lookup";

export interface EnrichmentResult {
    suggestions: AISuggestions | null;
    source: "lookup" | "llm" | null;
    loading: boolean;
    failed: boolean;
}

type MissingField = "frequency" | "administrationNotes" | "icdCodes" | "doses";

function mapLookupToSuggestions(entry: MedLookupEntry): AISuggestions {
    return {
        frequencies: entry.frequencies.map((f) => f.value),
        administrationNotes: entry.adminNotes.map((n) => n.value),
        icdCodes: entry.diagnoses.map((d) => ({ code: d.code, description: d.description })),
        doses: entry.dosages.map((d) => d.value)
    };
}

function mapDraftSuggestions(draft: MedicationDraft): AISuggestions | null {
    const suggestions = draft.normalizationSuggestions;
    if (!suggestions) {
        return null;
    }
    return {
        frequencies: suggestions.frequencies ?? [],
        administrationNotes: suggestions.administrationNotes ?? [],
        icdCodes: draft.suggestedIcdCodes ?? [],
        doses: suggestions.doses ?? []
    };
}

function computeMissingFields(draft: MedicationDraft): MissingField[] {
    const missing: MissingField[] = [];
    if (!draft.orderedDose) {
        missing.push("doses");
    }
    if (!draft.frequency) {
        missing.push("frequency");
    }
    if (!draft.administrationNotes) {
        missing.push("administrationNotes");
    }
    if (!draft.suggestedIcdCodes || draft.suggestedIcdCodes.length === 0) {
        missing.push("icdCodes");
    }
    return missing;
}

/**
 * Per-card enrichment hook. Handles the 2-step suggestion loading:
 * 1. Local medication lookup table (instant, 400 meds)
 * 2. LLM fallback via fetchAISuggestions (3-8s, only missing fields)
 *
 * @param draft - The medication draft to enrich
 * @param visitId - Current visit ID
 * @param enabled - false = queued (waiting for throttle slot), shows spinner
 * @param onLLMStart - Called when an LLM API call begins (for parent throttle counter)
 * @param onLLMEnd - Called when an LLM API call completes or fails
 */
export function useDraftEnrichment(
    draft: MedicationDraft,
    visitId: string,
    enabled: boolean,
    onLLMStart?: () => void,
    onLLMEnd?: () => void
): EnrichmentResult {
    const [result, setResult] = useState<EnrichmentResult>({
        suggestions: null,
        source: null,
        loading: false,
        failed: false
    });

    // Track whether we've already completed enrichment (don't re-fetch on re-render)
    const enrichedRef = useRef(false);
    // Track whether an LLM call is in flight (for cleanup)
    const llmActiveRef = useRef(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        // Skip if already enriched
        if (enrichedRef.current) {
            return;
        }

        // Supplement fields use the separate pill suggestion hook in DraftCard.
        if (draft.isSupplement) {
            setResult({ suggestions: null, source: null, loading: false, failed: false });
            enrichedRef.current = true;
            return;
        }

        const draftSuggestions = mapDraftSuggestions(draft);
        if (
            draftSuggestions &&
            (draftSuggestions.doses?.length ||
                draftSuggestions.frequencies.length ||
                draftSuggestions.administrationNotes.length ||
                draftSuggestions.icdCodes.length)
        ) {
            setResult({
                suggestions: draftSuggestions,
                source: "lookup",
                loading: false,
                failed: false
            });
            enrichedRef.current = true;
            return;
        }

        const missingFields = computeMissingFields(draft);

        // Nothing missing — card is complete from extraction
        if (missingFields.length === 0) {
            setResult({ suggestions: null, source: null, loading: false, failed: false });
            enrichedRef.current = true;
            return;
        }

        // Step 1: Try local medication lookup table (instant)
        const lookupEntry = findMedByName(draft.medicationName ?? draft.name);
        if (lookupEntry) {
            const suggestions = mapLookupToSuggestions(lookupEntry);
            setResult({ suggestions, source: "lookup", loading: false, failed: false });
            enrichedRef.current = true;
            return;
        }

        // Step 2: Need LLM — but are we enabled (have a throttle slot)?
        if (!enabled) {
            // Queued — show loading spinner, wait for slot
            setResult({ suggestions: null, source: null, loading: true, failed: false });
            return;
        }

        // Step 3: Fire LLM call
        setResult({ suggestions: null, source: null, loading: true, failed: false });

        if (!llmActiveRef.current) {
            llmActiveRef.current = true;
            onLLMStart?.();
        }

        fetchAISuggestions(
            visitId,
            draft.medicationName ?? draft.name,
            undefined,
            missingFields,
            true,
            draft.strength ?? draft.dosage ?? undefined,
            draft.orderedDose ?? undefined
        )
            .then((suggestions) => {
                if (!mountedRef.current) {
                    return;
                }
                setResult({ suggestions, source: "llm", loading: false, failed: false });
                enrichedRef.current = true;
            })
            .catch(() => {
                if (!mountedRef.current) {
                    return;
                }
                setResult({ suggestions: null, source: null, loading: false, failed: true });
                enrichedRef.current = true;
            })
            .finally(() => {
                if (llmActiveRef.current) {
                    llmActiveRef.current = false;
                    onLLMEnd?.();
                }
            });
    }, [draft, visitId, enabled, onLLMStart, onLLMEnd]);

    return result;
}
